import {CSSProperties, FC, ReactNode, useLayoutEffect, useRef, useState} from 'react'
import {fitFontSize, MIN_FONT_RATIO} from '../../helpers/fitText'

interface Props {
  /** The employee name (or template placeholder) — the line that gets long. */
  primary: ReactNode
  /** The position, under the name, in the position's colour. */
  secondary?: ReactNode
  secondaryColor?: string
  onPrimaryClick?: () => void
  primaryCursor?: CSSProperties['cursor']
  /**
   * Called with the height the row needs when the label wraps, or 0 when it
   * fits the normal row. The view grows that one row by it.
   */
  onNeededHeight?: (px: number) => void
  /**
   * False when `primary` is a control rather than text — the unassigned row's
   * "Assign employee…" picker. Only the position line is then sized and
   * wrapped; the picker keeps its own size and is never clipped, because a
   * clipped picker would cut off its own dropdown.
   */
  fitPrimary?: boolean
}

/**
 * The left-panel label of a scheduler row: name over position.
 *
 * A long name used to run past the 200px column or get clipped at the row's
 * fixed height. Now the text shrinks to fit the width — to no less than 80% of
 * its normal size — and when it still does not fit, it wraps and asks the view
 * for a taller row (`onNeededHeight`). Re-fits when the column changes width.
 */
export const ResourceLabel: FC<Props> = ({
  primary,
  secondary,
  secondaryColor,
  onPrimaryClick,
  primaryCursor,
  onNeededHeight,
  fitPrimary = true,
}) => {
  const boxRef = useRef<HTMLSpanElement>(null)
  const secondaryRef = useRef<HTMLSpanElement>(null)
  const [fit, setFit] = useState<{size: number | null; wrap: boolean}>({size: null, wrap: false})

  useLayoutEffect(() => {
    const box = boxRef.current
    if (!box) return

    const measure = () => {
      // Start from the size the panel gives its text (the parent's fs-7), so
      // the label never grows past it. Read from the PARENT: Bootstrap's fs-*
      // utilities are !important, so the label itself must not carry one or no
      // inline size would ever take effect.
      // What gets sized: the whole label, or only the position line when the
      // first line is a control.
      const target = fitPrimary ? box : secondaryRef.current
      if (!target) {
        onNeededHeight?.(0)
        return
      }
      target.style.whiteSpace = 'nowrap'
      const base = parseFloat(window.getComputedStyle(box.parentElement || box).fontSize) || 13
      const lines = fitPrimary ? (Array.from(box.children) as HTMLElement[]) : [target]
      const overflows = () =>
        (fitPrimary && box.scrollWidth > box.clientWidth + 1) ||
        lines.some((line) => line.scrollWidth > line.clientWidth + 1)
      const result = fitFontSize(
        (px) => {
          target.style.fontSize = `${px}px`
          return !overflows()
        },
        base,
        Math.round(base * MIN_FONT_RATIO * 100) / 100
      )
      target.style.fontSize = `${result.size}px`
      target.style.whiteSpace = result.wrap ? 'normal' : 'nowrap'
      setFit(result)

      if (onNeededHeight) {
        if (!result.wrap) {
          onNeededHeight(0)
        } else {
          const cell = box.closest('.rs__cell') as HTMLElement | null
          const style = cell ? window.getComputedStyle(cell) : null
          const padding = style
            ? (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0)
            : 0
          onNeededHeight(Math.ceil(box.scrollHeight + padding))
        }
      }
    }

    measure()
    // The weekly grid's first column is a percentage, so the room for the name
    // changes with the window. jsdom has no ResizeObserver.
    if (typeof ResizeObserver === 'undefined') return
    let lastWidth = box.clientWidth
    const observer = new ResizeObserver(() => {
      if (Math.abs(box.clientWidth - lastWidth) < 1) return
      lastWidth = box.clientWidth
      measure()
    })
    observer.observe(box)
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primary, secondary, fitPrimary])

  const lineStyle: CSSProperties = {
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: fit.wrap ? undefined : 'clip',
    overflowWrap: 'anywhere',
  }

  return (
    <span
      ref={boxRef}
      className='m-0 text-gray-900'
      data-testid='resource-label'
      data-wrapped={fit.wrap ? 'true' : 'false'}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: fitPrimary ? 'flex-start' : 'stretch',
        minWidth: 0,
        width: '100%',
        lineHeight: 1.25,
        ...(fitPrimary
          ? {
              whiteSpace: fit.wrap ? 'normal' : 'nowrap',
              ...(fit.size ? {fontSize: `${fit.size}px`} : {}),
            }
          : {}),
      }}
    >
      <span
        style={fitPrimary ? {...lineStyle, cursor: primaryCursor} : {cursor: primaryCursor}}
        onClick={onPrimaryClick}
      >
        {primary}
      </span>
      {secondary ? (
        <span
          ref={secondaryRef}
          className='fw-bold'
          style={{
            ...lineStyle,
            color: secondaryColor,
            ...(fitPrimary
              ? {}
              : {
                  whiteSpace: fit.wrap ? 'normal' : 'nowrap',
                  ...(fit.size ? {fontSize: `${fit.size}px`} : {}),
                }),
          }}
        >
          {secondary}
        </span>
      ) : null}
    </span>
  )
}
